import type { ConversationListItem } from '@/entities/conversations';
import type { ContentSearchMode } from '@/entities/settings';
import { TruncatedText } from '@/shared/ui';
import { ContentSnippet } from '../ContentSearch/ContentSnippet';
import { stripHeadlineHighlight } from '../ContentSearch/headlineHighlight';
import { EMPTY_CELL } from './constants';

/* Рендеры и метаданные колонок поиска по содержимому диалога (релевантность и фрагмент). */

/**
 * Настройки колонок поиска по содержимому диалога.
 *
 * Колонка фрагмента («Реплика»/«Комментарий») появляется при активном режиме, её вид
 * зависит от режима; колонка «Релевантность» — только при непустом запросе, без него
 * сервер ранга не считает.
 */
export type ContentSearchColumnOptions = {
  /** Режим, чьи колонки выводятся, или `null` — колонок поиска по содержимому нет */
  mode: ContentSearchMode | null;
  /** Запрос непустой: без него колонка «Релевантность» не выводится */
  hasQuery: boolean;
};

/** Ячейка «Релевантность» — числовое значение или пустая ячейка */
export const renderRelevanceCell = (value: number | null | undefined) => {
  if (value == null) return <span className="text-muted-foreground">{EMPTY_CELL}</span>;
  return <span className="font-roboto">{value.toFixed(3)}</span>;
};

const SNIPPET_CELL_CLASS = 'line-clamp-2';

/**
 * Ячейка фрагмента или пустая ячейка. `isHighlighted` — значение пришло от `ts_headline`
 * и содержит разметку подсветки; без него в ячейке лежит сырой текст комментария, который
 * парсер `<b>…</b>` исказил бы, встреться в тексте эти теги буквально.
 */
export const renderSnippetCell = (value: string | null | undefined, isHighlighted: boolean) => {
  if (!value) return <span className="text-muted-foreground">{EMPTY_CELL}</span>;
  if (!isHighlighted) return <TruncatedText lines={2}>{value}</TruncatedText>;
  return <ContentSnippet snippet={value} className={SNIPPET_CELL_CLASS} />;
};

/**
 * Состав колонок поиска по содержимому: фрагмент выводится всегда, «Релевантность» — только
 * при непустом запросе, потому что без запроса сервер ранга не возвращает и сортировку по нему
 * не принимает.
 */
export const pickContentSearchColumns = <TColumn,>(
  columns: { relevance: TColumn; snippet: TColumn },
  hasQuery: boolean,
): TColumn[] => (hasQuery ? [columns.relevance, columns.snippet] : [columns.snippet]);

/**
 * Текст фрагмента ровно в том виде, в каком он показан: подсветка совпавших слов снимается,
 * потому что наружу пользователь уносит текст реплики, а не разметку `ts_headline`.
 */
export const getSnippetPlainText = (value: string | null | undefined): string | null => {
  if (!value) return null;
  return stripHeadlineHighlight(value);
};

/** Идентификатор колонки фрагмента в зависимости от активного режима */
export const getSnippetColumnId = (mode: ContentSearchMode): 'matchedPhraseSnippet' | 'matchedCommentSnippet' =>
  mode === 'phrase' ? 'matchedPhraseSnippet' : 'matchedCommentSnippet';

/** Получает значение фрагмента из строки по активному режиму */
export const getSnippetValue = (row: ConversationListItem, mode: ContentSearchMode): string | null | undefined =>
  mode === 'phrase' ? row.matchedPhraseSnippet : row.matchedCommentSnippet;
